/**
 * M013 F3b→F5 — REJESTR KODOWAŃ TEKSTUR POLA (kontrakt radar-lab/us-field/1),
 * JEDNO źródło prawdy dla: enkodera pipeline'u (scripts/lib/field-texture.mjs),
 * rejestru palet renderu (src/rendering/render.mjs — kwantyzacja PRZED progiem,
 * W6) i klienta /us (src/us/usField.ts — walidacja manifestu i LUT shadera).
 *
 * Struktura bajtu WSPÓLNA dla wszystkich kodowań (schemat v1):
 *   sentinel 0 = NO-COVERAGE (state 3 silnika — poza zasięgiem radarów)
 *   sentinel 1 = MISSING     (state 2 silnika — w zasięgu, brak danych)
 *   bajty 2..255 = 254 poziomy wartości wg transformaty kodowania
 *
 * Transformaty:
 *   linear: byte = round((v + offset) × scale) + byteMin
 *           v    = (byte − byteMin) / scale − offset
 *   log10:  byte = round((log10(v) − offset) × scale) + byteMin
 *           v    = 10^(offset + (byte − byteMin) / scale)
 *           + FLOOR: v poniżej dolnej granicy siatki (w tym 0) koduje się do
 *             byteMin BEZ liczenia clampu — to zadeklarowana klasa „ślad
 *             poniżej progu widoczności palety" (dekoduje się do wartości
 *             pod progiem → LUT przezroczysta), nie utrata danych.
 *
 * KODOWANIE dBZ (kontrakt v2, probe Z1 2026-08-10 MEASURED, zakres dnia
 * [-25,5; +85] dBZ): liniowe, krok 0,5 dBZ == krok źródła (bezstratne),
 * zakres [-31,5; +95], margines ≥6 dBZ z obu stron.
 *
 * KODOWANIE mm/h (proba Z1-RATE 2026-08-11, MEASURED — szczegóły w
 * docs/milestones/M013-modern-radar-smoothing.md): źródło PrecipRate niesie
 * krok 0,1 mm/h (R=-30/E=0/D=1), rozkład silnie skośny (klasy palety
 * 0,1–150 mm/h rozpięte na 3 dekadach) — kodowanie LINIOWE zmiażdżyłoby
 * dolne klasy, stąd log10. Siatka ZAKOTWICZONA w 0,1 mm/h (pierwszy stop
 * palety RV): offset=-1,3, scale=60 ⇒ byte 20 == dokładnie 0,1 mm/h,
 * dekad na siatkę 60 poziomów (krok ×1,039 ≈ 3,9% — poniżej rozdzielczości
 * wizualnej rampy schodkowej), dół decode(2)=10^-1,3≈0,05 (przezroczysty
 * „ślad"), góra decode(255)=10^(2,9167)≈825 mm/h (margines >4× nad
 * obserwowanym maksimum). STRATNE względem kroku 0,1 przy dużych wartościach
 * (krok siatki ~3,9% wartości) — świadoma konwencja WIZUALIZACYJNA, spójna
 * serwer↔shader przez kwantyzację przed progiem (W6).
 */

export const FIELD_SCHEMA = "radar-lab/us-field/1";
export const FIELD_BYTE_MIN = 2;
export const FIELD_BYTE_MAX = 255;
export const FIELD_SENTINEL_NOCOVERAGE = 0;
export const FIELD_SENTINEL_MISSING = 1;

/** @typedef {{schema: string, units: string, transform: "linear"|"log10", offset: number, scale: number, byteMin: number, byteMax: number, sentinels: {noCoverage: number, missing: number}, note: string}} FieldEncoding */

/** @type {FieldEncoding} */
export const FIELD_ENCODING_DBZ = {
  schema: FIELD_SCHEMA,
  units: "dBZ",
  transform: "linear",
  offset: 31.5,
  scale: 2,
  byteMin: FIELD_BYTE_MIN,
  byteMax: FIELD_BYTE_MAX,
  sentinels: { noCoverage: FIELD_SENTINEL_NOCOVERAGE, missing: FIELD_SENTINEL_MISSING },
  note: "byte=round((dBZ+31.5)*2)+2; zakres [-31.5,+95]; sentinel 0=poza zasięgiem, 1=brak danych (stany RÓŻNE).",
};

/** @type {FieldEncoding} */
export const FIELD_ENCODING_RATE = {
  schema: FIELD_SCHEMA,
  units: "mm/h",
  transform: "log10",
  offset: -1.3,
  scale: 60,
  byteMin: FIELD_BYTE_MIN,
  byteMax: FIELD_BYTE_MAX,
  sentinels: { noCoverage: FIELD_SENTINEL_NOCOVERAGE, missing: FIELD_SENTINEL_MISSING },
  note:
    "byte=round((log10(mm/h)+1.3)*60)+2; siatka log zakotwiczona w 0.1 mm/h (byte 20); " +
    "wartości < dolnej granicy siatki (w tym 0) -> byte 2 = slad <0.1 (nie clamp-blad); " +
    "zakres [~0.05; ~825] mm/h; sentinel 0=poza zasięgiem, 1=brak danych.",
};

/** Rejestr per jednostka fizyczna — pipeline wybiera kodowanie po units profilu. */
const BY_UNITS = new Map([
  [FIELD_ENCODING_DBZ.units, FIELD_ENCODING_DBZ],
  [FIELD_ENCODING_RATE.units, FIELD_ENCODING_RATE],
]);

/**
 * Kodowanie dla jednostki fizycznej (null = jednostka bez kontraktu pola —
 * wołający MUSI głośno odmówić, nigdy nie zgadywać parametrów).
 * @param {string} units
 * @returns {FieldEncoding|null}
 */
export function fieldEncodingForUnits(units) {
  return BY_UNITS.get(units) ?? null;
}

/**
 * Wartość → bajt kontraktu. Dla log10: wartości poniżej dolnej granicy siatki
 * (w tym 0 i wartości nie-dodatnie) → byteMin BEZ flagi clamp (klasa „ślad",
 * patrz nagłówek). Clamp=true wyłącznie przy realnym wyjściu poza siatkę
 * (góra zawsze; dół tylko w trybie linear).
 * @param {number} v
 * @param {FieldEncoding} enc
 * @returns {{byte: number, clamped: boolean}}
 */
export function encodeFieldValue(v, enc) {
  let raw;
  if (enc.transform === "log10") {
    if (!(v > 0)) return { byte: enc.byteMin, clamped: false }; // 0/ujemne = ślad pod siatką
    raw = Math.round((Math.log10(v) - enc.offset) * enc.scale) + enc.byteMin;
    if (raw < enc.byteMin) return { byte: enc.byteMin, clamped: false }; // floor log10 — zadeklarowany
  } else {
    raw = Math.round((v + enc.offset) * enc.scale) + enc.byteMin;
    if (raw < enc.byteMin) return { byte: enc.byteMin, clamped: true };
  }
  if (raw > enc.byteMax) return { byte: enc.byteMax, clamped: true };
  return { byte: raw, clamped: false };
}

/**
 * Bajt kontraktu → wartość fizyczna (null dla sentineli).
 * @param {number} byte
 * @param {FieldEncoding} enc
 * @returns {number|null}
 */
export function decodeFieldByte(byte, enc) {
  if (byte === enc.sentinels.noCoverage || byte === enc.sentinels.missing) return null;
  if (enc.transform === "log10") return Math.pow(10, enc.offset + (byte - enc.byteMin) / enc.scale);
  return (byte - enc.byteMin) / enc.scale - enc.offset;
}

/**
 * Kwantyzacja wartości do siatki kodowania (decode∘encode) — używana przez
 * palety „field" PRZED progiem widoczności (jedna semantyka serwer↔shader, W6).
 * NaN przechodzi jako NaN (colorFor palet odmawia na NaN).
 * @param {number} v
 * @param {FieldEncoding} enc
 * @returns {number}
 */
export function quantizeFieldValue(v, enc) {
  if (Number.isNaN(v)) return NaN;
  return /** @type {number} */ (decodeFieldByte(encodeFieldValue(v, enc).byte, enc));
}

/**
 * Minimalna SUROWA wartość, której kwantyzacja osiąga próg widoczności palety
 * (próg klasy „value" do rejestru palet — bramka pikselowa opaquePx==value
 * musi klasyfikować IDENTYCZNIE jak colorFor po kwantyzacji).
 * Liczona z granicy zaokrąglenia siatki: dla linear T−0,25 stopnia siatki
 * jak w kontrakcie dBZ; dla log10 granica połówkowa w domenie logarytmicznej.
 * @param {FieldEncoding} enc
 * @param {number} firstStop pierwszy (najniższy) stop palety w jednostkach enc
 * @returns {number}
 */
export function fieldVisibilityThreshold(enc, firstStop) {
  // najniższy bajt, którego dekodowana wartość >= firstStop
  let k = null;
  for (let b = enc.byteMin; b <= enc.byteMax; b++) {
    const v = /** @type {number} */ (decodeFieldByte(b, enc));
    if (v >= firstStop) {
      k = b;
      break;
    }
  }
  if (k === null) throw new Error(`fieldVisibilityThreshold: żaden bajt kodowania ${enc.units} nie osiąga progu ${firstStop}`);
  // granica zaokrąglenia round-half-up między bajtem k-1 a k
  if (enc.transform === "log10") return Math.pow(10, enc.offset + (k - enc.byteMin - 0.5) / enc.scale);
  return (k - enc.byteMin - 0.5) / enc.scale - enc.offset;
}

/**
 * Metadane kontraktu do manifestu/sidecara (ADDYTYWNE — klient czyta stąd,
 * nigdy nie hardkoduje; walidacja kliencka porównuje z własnym rejestrem).
 * @param {FieldEncoding} enc
 * @returns {FieldEncoding}
 */
export function fieldEncodingMeta(enc) {
  return { ...enc, sentinels: { ...enc.sentinels } };
}
